import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  FiArrowRight,
  FiCheck,
  FiCopy,
  FiGift,
  FiUsers,
  FiZap,
} from "react-icons/fi";

import { colors } from "theme/colors";
import { analytics } from "MVVM/Analytics";
import { CLAIM_DAILY_CLIP, GET_CLIP_REWARDS } from "MVVM/GraphQL/rewards";
import { NAV_LINKS } from "MVVM/Components/Sidebar/utils";
import { BOOST_COPY, STEP_COPY, TBoost } from "MVVM/Utilities/Rewards/boosts";
import { useUser } from "MVVM/Hooks/useUser";
import { addAttributionParams } from "MVVM/Utilities/Attribution";
import { celebrate } from "MVVM/Utilities/Celebrate";
import {
  Action,
  ActionLink,
  AllRewards,
  Card,
  Claim,
  Daily,
  DailyToken,
  Foot,
  Meter,
  MeterBar,
  MeterFill,
  MeterRow,
  Reward,
  Row,
  Rows,
  SectionLabel,
  Streak,
  Sub,
  Tile,
  Title,
  Upgrade,
} from "./styledComponents";

interface IClipLimitModalProps {
  // Which creation surface hit the limit, so a retention change can be read
  // back to the surface that caused it (ALL-1967).
  surface: string;
  // Closes this modal alone, so a player who just earned a clip lands back on
  // the creation they were blocked out of rather than losing their place.
  onResume: () => void;
  onUpgrade: () => void;
}

const pad = (value: number) => String(value).padStart(2, "0");

const untilReset = (resetsAt?: string | null) => {
  if (!resetsAt) return null;
  const left = new Date(resetsAt).getTime() - Date.now();
  if (left <= 0) return "any moment now";
  const days = Math.floor(left / 86400000);
  const rest = `${pad(Math.floor((left % 86400000) / 3600000))}:${pad(
    Math.floor((left % 3600000) / 60000),
  )}:${pad(Math.floor((left % 60000) / 1000))}`;
  return days > 0 ? `${days}d ${rest}` : rest;
};

// The free day turns over at midnight Eastern, the same clock quentin counts on.
const easternClock = new Intl.DateTimeFormat("en-GB", {
  timeZone: "America/New_York",
  hour12: false,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const untilTomorrow = () => {
  const [hours, minutes, seconds] = easternClock
    .format(new Date())
    .split(":")
    .map(Number);
  const left = 86400 - (hours * 3600 + minutes * 60 + seconds);
  return `${pad(Math.floor(left / 3600))}:${pad(
    Math.floor(left / 60) % 60,
  )}:${pad(left % 60)}`;
};

export const ClipLimitModal = ({
  surface,
  onResume,
  onUpgrade,
}: IClipLimitModalProps) => {
  const { allstarUser } = useUser();
  const { data, error } = useQuery(GET_CLIP_REWARDS, {
    fetchPolicy: "cache-and-network",
  });
  const [claimDailyClip, { loading: claiming }] = useMutation(
    CLAIM_DAILY_CLIP,
    {
      refetchQueries: ["GetClipRewards"],
    },
  );
  const [copied, setCopied] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const claimButton = useRef<HTMLButtonElement>(null);
  const viewed = useRef(false);
  const [, setTick] = useState(0);

  const quota = data?.me?.clipQuotaNew;
  // Until the quota lands, the modal states no numbers rather than zeroes.
  const hasQuota = Boolean(quota);
  const handle = allstarUser?.user?.username || allstarUser?.user?._id;
  const name = allstarUser?.user?.username;
  const used = quota?.used ?? 0;
  const limit = quota?.limit ?? 0;
  const streak = quota?.streakDays ?? 0;
  const freeDaysLeft = (quota?.freeDayLimit ?? 0) - (quota?.freeDays ?? 0);
  const canClaim = Boolean(quota?.canClaimToday);
  const resetsIn = untilReset(quota?.resetsAt);
  // A finished boost is not a way out of the limit any more, so it drops off.
  const openBoosts: TBoost[] = (quota?.boosts ?? []).filter(
    (boost: TBoost) => !boost.completed,
  );

  useEffect(() => {
    const id = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) =>
      event.key === "Escape" && onResume();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onResume]);

  // Once per opening, but only after the quota has answered, so the view
  // carries real numbers. A failed load still counts as a view.
  useEffect(() => {
    if (viewed.current || (!quota && !error)) return;
    viewed.current = true;
    analytics.track("Clip Limit Modal - Viewed", {
      surface,
      quotaLoaded: Boolean(quota),
      tier: quota?.tier,
      used: quota?.used,
      limit: quota?.limit,
      streakDays: quota?.streakDays,
      freeDaysLeft,
      canClaimToday: Boolean(quota?.canClaimToday),
    });
  }, [quota, error, surface, freeDaysLeft]);

  const track = (lever: string) =>
    analytics.track("Clip Limit Modal - Earn Clicked", { surface, lever });

  // The click is intent, like every other lever; the grant is only logged once
  // the server confirms it, so a failed claim never counts as one.
  const claim = async () => {
    track("daily");
    const origin = claimButton.current?.getBoundingClientRect();
    let granted;
    try {
      granted = (await claimDailyClip()).data?.dailyClipClaim;
    } catch (e) {
      analytics.track("Daily Clip Claim Failed", { surface });
      return;
    }
    analytics.track("Daily Clip Claimed", {
      surface,
      streakDays: granted?.streakDays,
      freeDaysLeft: (granted?.freeDayLimit ?? 0) - (granted?.freeDays ?? 0),
    });
    setClaimed(true);
    celebrate(origin);
  };

  const resume = () => {
    analytics.track("Clip Limit Modal - Resumed", { surface });
    onResume();
  };

  const copyInvite = async () => {
    if (!handle) return;
    try {
      await navigator.clipboard.writeText(
        addAttributionParams(
          `${window.location.origin}/u/${handle}`,
          `${allstarUser?.user?._id}`,
        ),
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track("invite");
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <Card role="dialog" aria-modal="true" aria-labelledby="clip-limit-title">
      <Title id="clip-limit-title">
        {name ? `Hey ${name}, you` : "You"}&apos;ve used up all your clips.
      </Title>
      <Sub>
        {hasQuota && `You have made ${used} clips this month. `}
        But don&apos;t worry, there are ways to earn more clips.
      </Sub>

      <Meter>
        <MeterBar>
          <MeterFill $pct={hasQuota && limit ? (used / limit) * 100 : 0} />
        </MeterBar>
        <MeterRow>
          <span>
            {hasQuota ? (
              <>
                Your limit: <strong>{limit}</strong> clips a month
              </>
            ) : (
              "Checking your limit"
            )}
          </span>
          {resetsIn && (
            <span>
              Resets in <strong>{resetsIn}</strong>
            </span>
          )}
        </MeterRow>
      </Meter>

      {(freeDaysLeft > 0 || claimed) && (
        <Daily aria-live="polite">
          <DailyToken $claimed={claimed} aria-hidden="true">
            {claimed ? <FiCheck size={22} /> : <FiGift size={22} />}
          </DailyToken>
          <h3>
            <span>
              {claimed
                ? "Claimed, your clip is ready"
                : canClaim
                ? "Your free clip is ready"
                : "Come back tomorrow for your free clip"}
            </span>
            {streak > 1 && (
              <Streak>
                <FiZap size={11} aria-hidden="true" /> {streak} day streak
              </Streak>
            )}
          </h3>
          <p>
            {claimed
              ? "You can create now, and it does not come out of your limit."
              : `Your free clip does not come out of your limit. You have ${freeDaysLeft} more this month.`}
          </p>
          {claimed ? (
            <Claim type="button" onClick={resume}>
              Make your clip
            </Claim>
          ) : (
            <Claim
              ref={claimButton}
              type="button"
              onClick={claim}
              disabled={!canClaim || claiming}
            >
              {canClaim ? "Claim free clip" : `Unlocks in ${untilTomorrow()}`}
            </Claim>
          )}
        </Daily>
      )}

      <SectionLabel>More ways to earn</SectionLabel>
      <Rows>
        {openBoosts.map((boost) => {
          const copy = BOOST_COPY[boost.type];
          if (!copy) return null;
          const nextStep = boost.steps?.find((step) => !step.completed);
          const step = nextStep ? STEP_COPY[nextStep.step] : undefined;
          return (
            <Row key={boost.type}>
              <Tile $brand={copy.brand} aria-hidden="true">
                <copy.Icon size={22} />
              </Tile>
              <h4>
                {copy.short}{" "}
                <Reward>
                  +{boost.clips} <small>clips</small>
                </Reward>
              </h4>
              <p>{step ? `Next: ${step.label}` : copy.hook}</p>
              <ActionLink
                to={step?.to ?? NAV_LINKS.rewards}
                onClick={() => track(boost.type)}
              >
                Set up <FiArrowRight aria-hidden="true" />
              </ActionLink>
            </Row>
          );
        })}

        <Row>
          <Tile $brand={colors.chalkAlpha10} aria-hidden="true">
            <FiUsers size={22} />
          </Tile>
          <h4>
            Invite a friend{" "}
            <Reward>
              +5 <small>clips</small>
            </Reward>
          </h4>
          <p>You both get 5 clips once they sign up and link a game account.</p>
          <Action type="button" onClick={copyInvite} disabled={!handle}>
            {copied ? (
              <FiCheck aria-hidden="true" />
            ) : (
              <FiCopy aria-hidden="true" />
            )}
            {copied ? "Copied" : "Copy link"}
          </Action>
        </Row>
      </Rows>

      <Foot>
        <AllRewards to={NAV_LINKS.rewards} onClick={() => track("all rewards")}>
          See all ways to earn
        </AllRewards>
        <Upgrade
          type="button"
          onClick={() => {
            track("upgrade");
            onUpgrade();
          }}
        >
          Skip the limit with Unlimited
        </Upgrade>
      </Foot>
    </Card>
  );
};
